import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { ThemeProvider, CssBaseline, CircularProgress, Box, Typography } from '@mui/material'
import { theme } from './theme.ts'
import './index.css'
import { GenomicReport } from './components/GenomicReport/index.tsx'
//import { transformReport } from './utils/reportTransform.ts'
import { ResolveReport } from './resolvereport.ts'
import type { GenomicReport as GenomicReportType, PatientInfo, QueryVariantWithClinvar, RawReport } from './types/genomic.ts'

/*function FailedToLoadReport({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>
}*/

const ErrorIcon = () => (
  <svg color={theme.palette.primary.main} xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 21 21"><g fill="none" fillRule="evenodd" transform="translate(1 1)"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="m9.5.5l9 16H.5zm0 10v-5" /><circle cx="9.5" cy="13.5" r="1" fill="currentColor" /></g></svg>
)

interface ErrorDisplayProps {
  error: string | null;
}

function ErrorDisplay({ error }: ErrorDisplayProps) {
  return (
    <Box
      display="flex"
      justifyContent="center"
      flexDirection="column"
      alignItems="center"
      minHeight="100vh"
      p={3}
    >
      <Box sx={{ mb: 2 }}>
        <ErrorIcon />
      </Box>
      <Typography variant="h4" color="primary" gutterBottom>
        Unable to Load Report
      </Typography>
      <Typography variant="body1" paragraph textAlign="center">
        We encountered an issue while trying to load your report. Please try again or contact support if the problem persists.
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mt: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        {error || 'Unknown error occurred'}
      </Typography>
    </Box>
  )
}

function App() {
  const [report, setReport] = useState<GenomicReportType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [profile, setProfile] = useState<PatientInfo>({
    id: 'Unknown',
    name: 'Unknown',
    dateOfBirth: 'Unknown',
    sex: 'Unknown',
    sampleId: 'Unknown',
    collectionDate: 'Unknown',
  })
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data.action === "print") {
        window.print();
      }

      if (event.data.action === "profile") {
        setProfile(event.data.profile)
      }

      if (event.data.action === "scrollTo") {
        const sectionId = event.data.sectionId;
        const element = document.getElementById(sectionId);

        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          // Report back to parent that we scrolled to this section
          window.parent.postMessage({
            action: 'sectionVisible',
            sectionId: sectionId
          }, '*');
        } else {
          console.warn(`Section "${sectionId}" not found in document`);
        }
      }
    }

    // Set up scroll listener to report active section
    const handleScroll = () => {
      const sections = [
        'conditions_overview',
        'genetic_findings',
        'acmg_criteria',
        'glossary',
        'disclaimer'
      ];

      // Find which section is currently most visible
      let activeSection = '';
      let maxVisibility = 0;

      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;

          // Calculate how much of the element is visible
          const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
          const visibility = Math.max(0, visibleHeight) / viewportHeight;

          if (visibility > maxVisibility && rect.top < viewportHeight / 2) {
            maxVisibility = visibility;
            activeSection = sectionId;
          }
        }
      });

      if (activeSection) {
        window.parent.postMessage({
          action: 'sectionVisible',
          sectionId: activeSection
        }, '*');
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('scroll', handleScroll);
    }
  }, [])

  useEffect(() => {
    async function loadReport() {
      try {
        const reportId = new URLSearchParams(window.location.search).get('reportId')

        if (!reportId) {
          setReport(null)
          setLoading(false)
          return
        }

        const reportIdNumber = Number(reportId)

        if (isNaN(reportIdNumber)) {
          console.error('Invalid report ID')
          throw new Error('Invalid report ID')
        }

        const resolver = new ResolveReport(reportIdNumber)
        const reportData = await resolver.getReport()
        const variants = await resolver.getVariants()
        const metrics = resolver.metrics
       //const sample = await resolver.getSample()
        //const patient = await resolver.getPatient()

        const reportMeta = JSON.parse(reportData?.meta || "{}") as RawReport;
        //const cftrs = ['CFTR', 'CFTR2']
        const gVariants: QueryVariantWithClinvar[] = (variants || []);


        //const reportDate = new Date();
        //const formattedReportDate = reportDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

        //const dateOfBirth = patient?.dateOfBirth;
        /*const formattedDateOfBirth = dateOfBirth
          ? new Date(dateOfBirth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : '';*/

        //const collectionDate = patient?.collectionTime;
        /*const formattedCollectionDate = collectionDate
          ? new Date(collectionDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : '';*/

          const safeBigIntToNumber = (value: bigint | undefined, fallback: number = 0): number => {
            if (!value) return fallback;
            if (value > Number.MAX_SAFE_INTEGER) {
              console.warn(`Value ${value} exceeds MAX_SAFE_INTEGER, precision may be lost`);
            }
            return Number(value);
          };
        if (reportData && variants) {
          setReport({
            name: reportData.name,
            description: reportData.description,
            drugResponseVariants: [],
            conditions: reportMeta.clinical_summary.conditions,
            pathogenicVariants: gVariants,
            vusVariants: [],
            references: [],
            disclaimer: '',
            metrics: {
                variantsReturned: safeBigIntToNumber(metrics?.variantsReturned),
              variantsMatchedInVcf: safeBigIntToNumber(metrics?.variantsMatchedInVcf),
              totalTimeMs: safeBigIntToNumber(metrics?.totalTimeMs),
              filterTimeMs: safeBigIntToNumber(metrics?.filterTimeMs),
              vcfReadTimeMs: safeBigIntToNumber(metrics?.vcfReadTimeMs),
              variantsPerSecond: metrics?.variantsPerSecond || 0,
              vcfBytesRead: safeBigIntToNumber(metrics?.vcfBytesRead),
              vcfBlocksProcessed: safeBigIntToNumber(metrics?.vcfBlocksProcessed),
            },
            processingDetails: {
              date: "not implemented",
              pipeline: "not implemented",
              version: "not implemented",
              analyst: "not implemented"
            }
          } as GenomicReportType)
        }
      } catch (error) {
        console.error('Failed to load report:', error)
        
        setError(error instanceof Error ? error.message : 'Unknown error')
        setReport(null)
      } finally {
        setLoading(false)
      }
    }

    loadReport()
  }, [])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column"  >
        <CircularProgress />

        <Box sx={{ mt: 2 }}>
          <Typography variant="h4" color="primary" gutterBottom> 
            Updating report...
          </Typography>
        </Box>
      </Box>
    )
  }

  if (!report) {
    return <ErrorDisplay error={error} />
  }

  return <GenomicReport report={report} profile={profile} />
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <App />
    </ThemeProvider>
  </React.StrictMode>,
)
